import { cn } from 'src/components/ui/lib/utils';
import { useAppTranslation } from 'src/i18n/hooks';
import './styles.scss';
import { format6SignificantFigures } from 'src/helpers/formatNumber';

interface Props {
  open: boolean;
  onClose: () => void;
  points: number;
}

const CelebrationModal = ({ points, onClose, open }: Props) => {
  const { t } = useAppTranslation('leaderboard');

  return (
    <div
      className={cn([
        'fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm',
        open ? 'fixed' : 'hidden',
      ])}
    >
      {/* Confetti particles */}
      {Array.from({ length: 50 }, (_, i) => (
        <div
          key={i}
          className="absolute w-3 h-3 animate-confetti"
          style={{
            left: `${Math.random() * 100}%`,
            top: `-10%`,
            animationDelay: `${Math.random() * 2}s`,
            animationDuration: `${2 + Math.random() * 2}s`,
            backgroundColor: ['#22d3ee', '#a855f7', '#ec4899', '#10b981', '#f59e0b'][
              Math.floor(Math.random() * 5)
            ],
            transform: `rotate(${Math.random() * 360}deg)`,
          }}
        />
      ))}

      {/* Main banner */}
      <div className="relative bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-slate-800/95 border-4 border-cyan-400 rounded-2xl p-12 max-w-2xl w-full mx-4 shadow-2xl shadow-cyan-500/50 backdrop-blur-md">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute z-[2] cursor-pointer top-4 right-4 w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-full bg-slate-700/50 hover:bg-slate-600/50 border border-cyan-500/50 hover:border-cyan-400 transition-all duration-300 group"
        >
          <span className="text-cyan-400 text-2xl font-bold group-hover:text-white transition-colors">
            ×
          </span>
        </button>

        {/* Glow effect */}
        <div className="absolute inset-0 z-[1] rounded-2xl bg-gradient-to-r from-cyan-400/20 via-purple-500/20 to-pink-500/20 blur-xl animate-pulse"></div>

        {/* Content */}
        <div className="relative space-y-2 md:space-y-4 lg:space-y-6 text-center">
          {/* Success icon */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full blur-2xl opacity-50 animate-pulse"></div>
              <div className="relative text-2xl md:text-4xl lg:text-6xl animate-bounce">🎉</div>
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <h2 className="text-2xl lg:text-4xl font-black font-mono tracking-wider bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
              {t('congratulations')}!
            </h2>
            <p className="text-cyan-100 font-mono text-base tracking-wide">
              {t('youHaveSuccessClaimedPoints')}
            </p>
          </div>

          {/* Points display */}
          <div className="relative py-2 md:py-4 lg:py-8">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-xl blur-lg"></div>
            <div className="relative bg-slate-800/50 border-2 border-cyan-400/50 rounded-xl p-8">
              <div className="text-cyan-400 font-mono text-sm tracking-widest mb-2">
                {t('pointClaimed')}
              </div>
              <div className="text-2xl md:text-4xl lg:text-6xl font-black font-mono tracking-wider bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                +
                {Number(points ?? 0).toLocaleString('en-US', {
                  maximumFractionDigits: 4,
                  minimumFractionDigits: 0,
                })}
              </div>
            </div>
          </div>

          {/* Decorative elements */}
          <div className="flex justify-center gap-4 text-2xl">
            <span className="animate-bounce" style={{ animationDelay: '0s' }}>
              ⚡
            </span>
            <span className="animate-bounce" style={{ animationDelay: '0.2s' }}>
              🔥
            </span>
            <span className="animate-bounce" style={{ animationDelay: '0.4s' }}>
              💎
            </span>
            <span className="animate-bounce" style={{ animationDelay: '0.6s' }}>
              🚀
            </span>
          </div>

          {/* Close button text */}
          <div className="pt-1 md:pt-2 lg:pt-4">
            <p className="text-slate-400 font-mono text-sm">{t('clickXToContinue')}</p>
          </div>
        </div>

        {/* Animated border particles */}
        {Array.from({ length: 8 }, (_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-cyan-400 rounded-full animate-ping"
            style={{
              left: `${12.5 + i * 12.5}%`,
              top: i % 2 === 0 ? '-4px' : 'auto',
              bottom: i % 2 === 1 ? '-4px' : 'auto',
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default CelebrationModal;
